import React, { useEffect, useState } from 'react';
import { addDays, addHours, endOfDay, isSameDay, startOfDay, } from 'date-fns';
import TimelineRangeSlider from "../../../../Components/TimelineRangeSlider/TimelineRangeSlider";
import AvailabilityTimelineDateSwitcher from "../../../../Components/Inputs/Availability/AvailabilityTimelineDateSwitcher";
import AvailabilityIntervalSelector from "../../../../Components/Inputs/Availability/AvailabilityIntervalSelector";
import { intervalArrayFromState, intervalFromState, intervalToState, isWithinInterval, setIntervalDatesButKeepTimes } from "../../../../Helpers/IntervalHelper";
import { dateFromState } from "../../../../Helpers/DateTimeHelper";
import {
    extendAvailabilityBoundary,
    getDefaultAvailabilitySlot,
    getDefaultTimelineInterval
} from "../../../../Common/Availability/Availability";
import AvailabilityCheckWrapper from "./AvailabilityCheckWrapper";
import { validateInterval, isOpenOnThisDay } from "../../../../Common/Availability/AvailabilityValidation";

export const TIMELINE_HOURS_PER_PAGE = 10;

const getTimelineInterval = (availabilityDate) => {
    availabilityDate = dateFromState(availabilityDate);
    return intervalToState({
        start: availabilityDate,
        end: addHours(availabilityDate, TIMELINE_HOURS_PER_PAGE)
    });
}

const AmenityVariableAvailability = (props) => {

    const [timelineInterval, setTimelineInterval] = useState(null);
    const [selectedInterval, setSelectedInterval] = useState(props.selectedInterval);

    // TODO: We don't need two state vars for an error. Error message is sufficient by itself
    const [intervalError, setIntervalError] = useState(false);
    const [intervalErrorMsg, setIntervalErrorMsg] = useState(null);

    /**
     * Choose a default interval selection and timelineInterval if there isn't one yet
     */
    useEffect(() => {
        // Don't try and do anything if we haven't retrieved availability yet
        if (!props.availability?.availableIntervals) {
            return;
        }

        /** See if we need to change selected interval **/
        if (selectedInterval && selectedInterval.start) {
            var newInterval = selectedInterval;

            // Change interval if we've changed booking date, as we don't want to still be selecting the previous day
            if (!isSameDay(dateFromState(selectedInterval.start), dateFromState(props.bookingDate))) {
                newInterval = getDefaultAvailabilitySlot(props.bookingDate, props.availability.availableIntervals)
                handleIntervalChange(newInterval, false, 'useEffect with selected interval')
            }
        } else {
            // We don't have an interval yet, set a default
            newInterval = getDefaultAvailabilitySlot(props.bookingDate, props.availability.availableIntervals)
            handleIntervalChange(newInterval, false, 'useEffect without selected interval');
        }

        /** Now see if we need to set the timeline interval **/
        let thisTimelineInterval;

        if (!timelineInterval
            || !isSameDay(dateFromState(timelineInterval.start), dateFromState(props.bookingDate))
        ) {
            thisTimelineInterval = getDefaultTimelineInterval(props.availability.openingHours, props.bookingDate, newInterval);
            setTimelineInterval(thisTimelineInterval);
        } else {
            thisTimelineInterval = timelineInterval;
        }

        if (!isOpenOnThisDay(thisTimelineInterval.start, props.availability.openingHours)) {
            handleTimelineError(true, 'Amenity is closed on this day');
        }

    }, [props.availability.boundary.start])


    useEffect(() => {
        handleValidateInterval(selectedInterval);
    }, [props.bookingDate, selectedInterval]);

    /**
     * Validate interval on timeline change
     * @param interval
     */
    const handleValidateInterval = (interval) => {
        let thisBookingDate = dateFromState(props.bookingDate);

        // TODO - what's happening with this?
        let boundary = {
            start: startOfDay(thisBookingDate),
            end: endOfDay(addDays(thisBookingDate, 1))
        };

        const {isValid, errors} = validateInterval(
            interval,
            props.bookingConfig,
            props.availability,
        );

        if (!isValid) {
            handleTimelineError(true, errors[0]);
        } else {
            handleTimelineError(false, null);
        }
    }

    const shouldShowNow = () => {
        return isWithinInterval(new Date(), timelineInterval);
    }

    const handleTimelineError = (error, message) => {
        setIntervalError(error);
        setIntervalErrorMsg(message);

        if (props.onError) {
            props.onError(error, message);
        }
    }


    const handleBookButtonClick = () => {
        if (props.showBookButton) {
            props.onChange(selectedInterval);
        }
    }

    /**
     * @param interval  Interval here is an array of two JS DATE OBJECTS
     */
    const handleIntervalChange = (interval) => {
        interval = intervalToState(interval);

        // Do not allow updates while loading new configs, otherwise the
        // timeline component will trigger updates while timeline intervals etc
        // are changing, which will cause allsorts of hassle, like selections
        // being reset to the previous day
        if (props.isLoading) {
            return;
        }

        if (!interval) {
            setSelectedInterval(interval);
            return;
        }

        setSelectedInterval(interval);

        handleValidateInterval(interval);

        if (!props.showBookButton) {
            props.onChange(interval)
        }

        // If we've chosen an interval on another day, change the booking date
        if (!isSameDay(
            dateFromState(props.bookingDate),
            dateFromState(interval.start)
        )) {
            props.onBookingDateChange(interval.start);
        }
    };

    const handleBookingDateChange = (newDate) => {
        setTimelineInterval(getTimelineInterval(newDate));

        let currentBoundary = props.availability.boundary;
        let boundaryThreshold = {start: startOfDay(new Date()), end: null};
        let newBoundary = extendAvailabilityBoundary(currentBoundary, newDate, boundaryThreshold, 24);

        if (newBoundary.start !== currentBoundary.start
            || newBoundary.end !== currentBoundary.end
        ) {
            props.onTimelineBoundaryChange(newBoundary);
        }
    }

    const handleResetTimelineClick = (event) => {
        let resetTimelineInterval = getDefaultTimelineInterval(props.availability.openingHours, props.bookingDate, selectedInterval);
        setTimelineInterval(resetTimelineInterval);
    }

    // We cannot attempt to render the timeline component without a selectedInterval
    // because it will crash. You need to set a default selectedInterval
    if (!selectedInterval || !selectedInterval.start || !timelineInterval || !timelineInterval.start) {
        return null;
    }

    return (<>

        <TimelineRangeSlider
            // Convert the intervals for this component into date object format
            timelineInterval={intervalFromState(timelineInterval)}
            blockedIntervals={intervalArrayFromState(props.availability.blockedIntervals)}
            selectedInterval={intervalFromState(selectedInterval)}
            onChange={handleIntervalChange}

            step={900000}
            maxDuration={props.maxDuration}
            allowPast={props.allowPast}
            error={intervalError}
            showNow={shouldShowNow()}
            showHandles={false}
        />

        <AvailabilityTimelineDateSwitcher
            hoursPerPage={TIMELINE_HOURS_PER_PAGE/2}
            timelineDate={timelineInterval.start}
            bookingDate={props.bookingDate}
            selectedInterval={selectedInterval}
            errorMessage={intervalErrorMsg}
            isLoading={props.isLoading}
            onBookingDateChange={handleBookingDateChange}
            onResetDateClick={handleResetTimelineClick}
        />


        <AvailabilityCheckWrapper
            bookingConfig={props.bookingConfig}
            availability={props.availability}
            bookingDate={props.bookingDate}
            amenityId={props.amenityId}
            unitId={props.unitId}
            onBookingDateChange={props.onBookingDateChange}
            showNextAvailabilityButton={props.showNextAvailabilityButton}
        >
            <div className="grid-x align-bottom">
                <div className={`small-12 medium-${props.showBookButton ? 8 : 12}`}>
                    <AvailabilityIntervalSelector
                        bookingDate={props.bookingDate}
                        availableIntervals={props.availability.availableIntervals}
                        blockedIntervals={props.availability.blockedIntervals}
                        selectedInterval={selectedInterval}
                        maxDuration={props.maxDuration}
                        onIntervalChange={handleIntervalChange}
                        isEditingExistingBooking={props.isEditingExistingBooking}
                        startTimeErrorMessages={props.startTimeErrorMessages}
                    />
                </div>

                { props.showBookButton &&
                    <div className="small-12 medium-4 text-right">
                        <button
                            className="button primary mb-0"
                            disabled={intervalError}
                            onClick={handleBookButtonClick}
                        >
                            Book
                        </button>
                    </div>
                }
            </div>
        </AvailabilityCheckWrapper>
    </>)
}

AmenityVariableAvailability.defaultProps = {
    bookingDate: null,
    selectedInterval: [],
    onUpdateCallback: null,
    onChangeCallback: null,
    onButtonClick: null,
    onBookingDateChange: null,
    maxDuration: 0,
    showBookButton: false,
    showNextAvailabilityButton: true,
    allowPast: true,
    isLoading: false,
}

export default AmenityVariableAvailability;
